import React, { useEffect, useState } from 'react';
import './Toast.css';
import {
	DEFAULT_SUCCESS_TOAST_DURATION,
	DEFAULT_TOAST_DURATION,
	__,
	FeedBackType,
	ToastData,
	IoCloseCircle,
	DEFAULT_ANIMATION_DURATION,
	FeedBackConfig
} from '@/Shared';


/**
 * ToastProps interface
 *
 * Defines the props for the Toast component.
 */
interface ToastProps {
	/** Toast data containing message, type, and optional duration */
	data?: ToastData | null;

	/** Callback triggered when the toast is dismissed */
	onClose: () => void;

	/** Determines if the toast can be closed manually by the user */
	dismissible?: boolean;
}

/**
 * Toast component
 *
 * Reusable notification component that displays feedback messages
 * (success, error, info, warning). Visibility is entirely controlled
 * by the presence of `data`. Toast automatically disappears after
 * a configurable duration and supports manual dismissal.
 *
 * Author: Samano CASTRE
 * Date: January 20, 2026
 */
const Toast: React.FC<ToastProps> = ({ data, onClose, dismissible = true }) => {

	const [visible, setVisible] = useState(!!data);

	// State to trigger fade-out animation
	const [fadeOut, setFadeOut] = useState("");

	// Determine the type of toast (default: info)
	const type = data?.type ?? FeedBackType.INFO;

	// Determine duration for auto-dismiss
	const duration = data?.duration ?
		data.duration : type === FeedBackType.SUCCESS ?
			DEFAULT_SUCCESS_TOAST_DURATION : DEFAULT_TOAST_DURATION;

	/**
	 * Automatically dismiss the toast after the specified duration
	 * using a fade-out animation.
	 */
	useEffect(() => {
		if(!data) return;
		setFadeOut('');
		setVisible(true);
		const timer = setTimeout(animateClose, duration);
		return () => clearTimeout(timer);
	}, [duration, data]);

	// Do not render if no data is provided
	if (!data) {
		return null;
	}
	/**
	 * Triggers the fade-out animation and calls the onClose callback
	 * after the animation duration has elapsed.
	 */
	const animateClose = () => {
		setFadeOut('fade-out');
		setTimeout(onClose, DEFAULT_ANIMATION_DURATION);
	}


	return (
		<div
			className={`Toast feedback feedback--${type} elevation-1 ${fadeOut}`}
			role="alert"
			aria-live="assertive">
			<span className="Toast__icon">{FeedBackConfig[type].icon}</span>
			<span className="Toast__message">{__(data.message)}</span>
			{
				dismissible && (
					<button
						className="custom-close-btn" onClick={animateClose}
						aria-label="Close alert">
						<IoCloseCircle />
					</button>
				)
			}
		</div>
	);
};

export default Toast;
